import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProducts, createProduct, updateProduct, deleteProduct } from './productSlice';
import { getPrimaryImageUrl, getFallbackImageUrl } from '../../utils/imageUtils';
import Shell from '../dashboard/Shell';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import Sidebar from '../../components/ui/Sidebar';
import ProductForm from './ProductForm';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import { QRCodeDisplay } from '../../components/QRCode';
import api, { apiFetch } from '../../utils/api';
import {
  Package,
  Plus,
  Download,
  Upload,
  Search,
  Edit,
  Trash2,
  QrCode,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  Lock,
  Sparkles,
  ChevronDown,
  ImageIcon,
  Check,
  Archive,
  X as XIcon,
  ClipboardList
} from 'lucide-react';
import './Products.css';

/**
 * Filter dropdown — custom replacement for the native <select>.
 *
 * Mobile <select> pickers are wildly inconsistent across browsers
 * (iOS wheel at the bottom, Android centered modal, some browsers
 * popup at the viewport top regardless of where the trigger lives).
 * A custom dropdown gives us a predictable popover that opens right
 * below the trigger on every platform. Positions via
 * getBoundingClientRect + `position: fixed` (see `.dropdown-menu`)
 * so the table wrapper's `overflow-x: auto` doesn't clip it.
 *
 * Props:
 *   - value: currently selected value
 *   - options: [{ value, label }]
 *   - onChange: (value) => void
 *   - ariaLabel: accessible label for the trigger
 */
function FilterDropdown({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapperRef = useRef(null);

  // Close on outside click. mousedown beats click so the close fires
  // before any other onClick can re-open the same dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = (e) => {
    if (open) { setOpen(false); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 220;
    const flipUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    setPos(flipUp
      ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, top: 'auto', minWidth: rect.width }
      : { top: rect.bottom + 4, left: rect.left, bottom: 'auto', minWidth: rect.width });
    setOpen(true);
  };

  const currentLabel = (options.find(o => o.value === value) || options[0]).label;

  return (
    <div className="prod-filter-dropdown" ref={wrapperRef}>
      <button
        type="button"
        className="prod-filter-trigger"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{currentLabel}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="dropdown-menu" role="listbox" style={pos}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              className={`dropdown-item ${value === opt.value ? 'active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Merchant products list page.
 *
 * Layout (see Products.css for the new design language):
 *   - Header: title + meta + action buttons (Export / Import / Add).
 *     On <768px the action row collapses into a mobile dropdown.
 *   - Stats: 4 quiet white cards (retail value, inventory value, sold, OOS).
 *   - List card: filter bar, then either a clean desktop table or a
 *     stacked mobile card list, plus pagination.
 *   - Sidebars / modals: ProductForm sidebar, Import modal, QR modal,
 *     and a tier upgrade modal. All preserved from the previous version
 *     so business logic (Redux dispatches, CSV import flow, QR codes,
 *     tier checks, quick-edit dropdowns) is unchanged.
 *
 * Business logic in this file:
 *   - Fetches product list + product limit + categories on mount.
 *   - Local state for search/sort/status/stock filters + pagination.
 *   - Bulk select (header checkbox + per-row checkboxes).
 *   - Quick-edit dropdowns to flip a product's category or status
 *     inline without opening the form.
 *   - CSV import + sample CSV download.
 *   - Tier-limit gating for Add / Import (warns + shows upgrade modal).
 */
export default function Products() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, totalSold, status, error } = useSelector(state => state.products);
  const { confirm } = useConfirm();
  const toast = useToast();

  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addSuccess, setAddSuccess] = useState(null);

  // Quick edit state — single dropdown open at a time, keyed by
  // `{productId, type}` (type ∈ 'category' | 'status').
  const [quickEditProduct, setQuickEditProduct] = useState(null);
  const [quickEditType, setQuickEditType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedProductForQR, setSelectedProductForQR] = useState(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProgress, setImportProgress] = useState({ importing: false, success: 0, failed: 0, total: 0 });
  const [importErrors, setImportErrors] = useState([]);

  // Bulk select state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Product limits state
  const [productLimits, setProductLimits] = useState(null);
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Pagination & Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy] = useState('name');
  const [sortOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const itemsPerPage = 25;

  // Fetch product limits — extracted so it can be re-called after a
  // create or delete to keep the meta-row count fresh.
  const fetchProductLimits = async () => {
    try {
      const response = await api('/merchant/product-limits');
      if (response && response.ok) {
        const data = await response.json();
        if (data && data.success) {
          setProductLimits(data);
        }
      }
    } catch (error) {
      // Silently fail — product limits are optional, don't break the page
      console.warn('Could not fetch product limits:', error?.message || error);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    fetchProductLimits();
    const loadCategories = async () => {
      try {
        const response = await apiFetch('/merchants/categories?tree=true');
        const data = await response.json();
        if (response.ok && data.data) {
          setCategories(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    loadCategories();
  }, [dispatch]);

  // Quick category/status change handler
  const handleQuickChange = async (productId, field, value) => {
    try {
      const updateData = { [field]: value };
      if (field === 'categoryId') {
        // Also update the category string for backwards compat
        const cat = categories.find(c => c.id === value) ||
                    categories.flatMap(c => c.children || []).find(c => c.id === value);
        if (cat) updateData.category = cat.name;
      }
      await dispatch(updateProduct({ id: productId, product: updateData })).unwrap();
      toast.success(`Product ${field === 'status' ? 'status' : 'category'} updated`);
      dispatch(fetchProducts());
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setQuickEditProduct(null);
      setQuickEditType(null);
    }
  };

  // Close quick edit dropdown when clicking outside any cell tagged
  // with the `data-quick-edit` attribute.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (quickEditProduct && !e.target.closest('[data-quick-edit]')) {
        setQuickEditProduct(null);
        setQuickEditType(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [quickEditProduct]);

  // Close the mobile actions dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMobileActions && !e.target.closest('.prod-mobile-actions')) {
        setShowMobileActions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMobileActions]);

  // Compute the quick-edit dropdown position in viewport coords.
  //
  // The dropdown uses `position: fixed` (see Products.css) so that
  // the table wrapper's `overflow-x: auto` — which implicitly
  // clips overflow-y — doesn't crop it. With fixed positioning we
  // have to pass real viewport pixels for `top`/`left`, picked
  // from the trigger's getBoundingClientRect at click time. If
  // there's no room below the trigger, we flip and pin to bottom.
  const getDropdownPosition = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 200;
    const flipUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    return flipUp
      ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, top: 'auto' }
      : { top: rect.bottom + 4, left: rect.left, bottom: 'auto' };
  };

  const handleQuickEditClick = (e, productId, type) => {
    e.stopPropagation();
    const position = getDropdownPosition(e);
    setDropdownPosition({ [productId + type]: position });
    setQuickEditProduct(quickEditProduct === productId && quickEditType === type ? null : productId);
    setQuickEditType(quickEditProduct === productId && quickEditType === type ? null : type);
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalRetailValue = products.reduce((sum, product) => sum + (parseFloat(product.price) || 0), 0);
    const totalInventoryValue = products.reduce((sum, product) => sum + (parseFloat(product.price) * (product.quantity || 0)), 0);
    const outOfStock = products.filter(product => (product.quantity || 0) === 0).length;
    return {
      totalRetailValue,
      totalInventoryValue,
      productsSold: totalSold || 0,
      outOfStock
    };
  }, [products, totalSold]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    // Deduplicate by id (some endpoints emit duplicates between create + fetch)
    const uniqueProducts = products.reduce((acc, product) => {
      const id = product.id || product._id;
      if (!acc.some(p => (p.id || p._id) === id)) {
        acc.push(product);
      }
      return acc;
    }, []);

    let filtered = uniqueProducts.filter(product => {
      if (!product) return false;
      const productName = product.name || '';
      const productDescription = product.description || '';
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = productName.toLowerCase().includes(searchLower) ||
        productDescription.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'published' && product.status?.toUpperCase() === 'ACTIVE') ||
        (statusFilter === 'draft' && product.status?.toUpperCase() === 'DRAFT') ||
        (statusFilter === 'archived' && product.status?.toUpperCase() === 'ARCHIVED');
      const matchesStock = stockFilter === 'all' ||
        (stockFilter === 'in_stock' && (product.quantity || 0) > 0) ||
        (stockFilter === 'out_of_stock' && (product.quantity || 0) === 0);
      return matchesSearch && matchesStatus && matchesStock;
    });

    filtered.sort((a, b) => {
      let aVal = a[sortBy] ?? '';
      let bVal = b[sortBy] ?? '';
      if (sortBy === 'price' || sortBy === 'quantity') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [products, searchTerm, sortBy, sortOrder, statusFilter, stockFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredAndSortedProducts.slice(startIndex, startIndex + itemsPerPage);
  const visibleStart = filteredAndSortedProducts.length === 0 ? 0 : startIndex + 1;
  const visibleEnd = Math.min(startIndex + itemsPerPage, filteredAndSortedProducts.length);

  // Reset selected products when the visible slice changes
  useEffect(() => {
    setSelectedProducts([]);
  }, [currentPage, searchTerm, statusFilter, stockFilter]);

  // Bulk select handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProducts(paginatedProducts.map(p => p.id || p._id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const isAllSelected = paginatedProducts.length > 0 && selectedProducts.length === paginatedProducts.length;
  const isSomeSelected = selectedProducts.length > 0 && selectedProducts.length < paginatedProducts.length;

  // Product submit (used by the legacy sidebar form path; the new
  // primary flow navigates to /products/new and /products/:id/edit).
  const onSubmitProduct = async (productData) => {
    try {
      setAdding(true);
      setAddSuccess(null);
      if (editingId && editingId !== 'new') {
        await dispatch(updateProduct({ id: editingId, product: productData })).unwrap();
        setAddSuccess('Product updated successfully!');
        dispatch(fetchProducts());
      } else {
        await dispatch(createProduct({ product: productData })).unwrap();
        setAddSuccess('Product added successfully!');
        dispatch(fetchProducts());
      }
      setShowModal(false);
      setEditingId(null);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setAdding(false);
    }
  };

  // Tier-limit helpers
  const isLimitReached = productLimits && !productLimits.unlimited && !productLimits.allowed;

  const handleAddProduct = () => {
    if (isLimitReached) {
      toast.warning(`Product limit reached (${productLimits?.currentCount || 0}/${productLimits?.limit || 0}). Upgrade your plan to add more products.`, 5000);
      // Route straight to /upgrade/checkout for the tier the backend
      // says unlocks more products — skips the plan-picker screen so the
      // merchant goes from limit-hit → summary → pay in one tap.
      navigateToUpgrade(navigate, productLimits?.nextTier);
      return;
    }
    navigate('/products/new');
  };

  const handleInventoryReport = () => {
    navigate('/products/inventory-report');
  };

  const handleImportClick = () => {
    if (isLimitReached) {
      toast.warning(`Product limit reached (${productLimits?.currentCount || 0}/${productLimits?.limit || 0}). Upgrade your plan to import products.`, 5000);
      navigateToUpgrade(navigate, productLimits?.nextTier);
      return;
    }
    setShowImportModal(true);
  };

  const handleEditProduct = (product) => {
    const productId = product.id || product._id;
    if (productId) {
      navigate(`/products/${encodeURIComponent(productId)}/edit`);
      return;
    }
    setEditingId(productId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Are you sure you want to delete this product?', {
      title: 'Delete Product',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    if (!confirmed) return;
    try {
      setDeleting(id);
      await dispatch(deleteProduct({ id })).unwrap();
      fetchProductLimits();
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleting(null);
    }
  };

  // ── Bulk actions ────────────────────────────────────────────────────
  //
  // Each handler runs the matching single-product thunk against every
  // selected id in parallel via Promise.allSettled — partial failure
  // doesn't undo the successes. We refetch the list once at the end
  // (rather than per-row) to keep the round-trips down, and clear the
  // selection so the bulk bar dismisses itself.

  const runBulkStatusUpdate = async (nextStatus, verb) => {
    const ids = [...selectedProducts];
    if (ids.length === 0) return;
    const results = await Promise.allSettled(
      ids.map(id => dispatch(updateProduct({ id, product: { status: nextStatus } })).unwrap())
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    const ok = ids.length - failed;
    if (failed === 0) {
      toast.success(`${ok} product${ok === 1 ? '' : 's'} ${verb}`);
    } else if (ok > 0) {
      toast.warning(`${ok} ${verb}, ${failed} failed`);
    } else {
      toast.error(`Failed to ${verb} ${ids.length} product${ids.length === 1 ? '' : 's'}`);
    }
    dispatch(fetchProducts());
    setSelectedProducts([]);
  };

  const handleBulkPublish = () => runBulkStatusUpdate('ACTIVE', 'published');
  const handleBulkArchive = () => runBulkStatusUpdate('ARCHIVED', 'archived');

  const handleBulkDelete = async () => {
    const ids = [...selectedProducts];
    if (ids.length === 0) return;
    const confirmed = await confirm(
      `Delete ${ids.length} product${ids.length === 1 ? '' : 's'}? This can't be undone.`,
      { title: 'Delete products', confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' }
    );
    if (!confirmed) return;
    const results = await Promise.allSettled(
      ids.map(id => dispatch(deleteProduct({ id })).unwrap())
    );
    const failed = results.filter(r => r.status === 'rejected').length;
    const ok = ids.length - failed;
    if (failed === 0) {
      toast.success(`${ok} product${ok === 1 ? '' : 's'} deleted`);
    } else if (ok > 0) {
      toast.warning(`${ok} deleted, ${failed} failed`);
    } else {
      toast.error(`Failed to delete ${ids.length} product${ids.length === 1 ? '' : 's'}`);
    }
    fetchProductLimits();
    setSelectedProducts([]);
  };

  // CSV export — builds a CSV from the in-memory product list and
  // triggers a browser download. Same flow as before, factored into a
  // function so both desktop + mobile triggers can call it.
  const handleExportCSV = () => {
    if (!products || products.length === 0) {
      toast.info('No products to export');
      return;
    }
    // Column order + names match handleDownloadSampleCSV so an exported
    // file can be edited and re-imported without header drift. The field
    // is `quantity` on the Product model — `stock` doesn't exist, and
    // exporting it silently produced blanks that broke re-import.
    const csvData = products.map(product => ({
      name: product.name,
      price: product.price,
      category: product.category,
      status: product.status,
      quantity: product.quantity ?? 0,
      sku: product.sku,
    }));
    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Download sample CSV template
  const handleDownloadSampleCSV = () => {
    // `costPrice` seeds the opening-stock ProductBatch cost when the
    // server auto-creates it. Leaving it blank made every imported
    // product bill its cost equal to its selling price — quietly wrong
    // margin numbers. Column is optional; blank falls back to price.
    const sampleData = [
      ['name', 'description', 'price', 'compareAtPrice', 'costPrice', 'quantity', 'sku', 'category', 'productType', 'status', 'tags', 'imageUrl'],
      ['Sample Product 1', 'Description for physical product', '5000', '6000', '3200', '100', 'SKU001', 'clothing', 'physical', 'ACTIVE', 'tag1,tag2', 'https://example.com/image1.jpg'],
      ['Sample Digital Product', 'Digital download product', '2500', '', '', '999', 'DIG001', 'electronics', 'digital', 'ACTIVE', 'digital,download', 'https://example.com/image2.jpg'],
      ['Sample Service', 'Service offering description', '15000', '', '', '50', 'SVC001', 'beauty', 'service', 'ACTIVE', 'service,premium', 'https://example.com/image3.jpg'],
      ['Sample Appointment', 'Consultation booking', '10000', '', '', '100', 'APT001', 'other', 'appointment', 'ACTIVE', 'consultation,booking', 'https://example.com/image4.jpg'],
    ];
    const csvContent = sampleData.map(row => row.map(cell => {
      if (cell.includes(',') || cell.includes('"')) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return cell;
    }).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Count non-empty data rows in a CSV without a full parse. Used for
  // the tier pre-flight check so we can refuse an over-limit upload
  // before sending the file — no partial imports, no wasted bandwidth.
  const countCsvRows = async (file) => {
    const text = await file.text();
    // Strip BOM + normalise line endings so a Windows-edited file
    // doesn't skew the count.
    const normalised = text.replace(/^﻿/, '').replace(/\r\n/g, '\n');
    const lines = normalised.split('\n').filter(l => l.trim().length > 0);
    return Math.max(0, lines.length - 1); // minus header row
  };

  // Handle CSV import.
  //
  // Flow: count rows → preflight tier check → upload file to bulk-import
  // → render structured per-row report from the server. Parsing lives on
  // the server so header/quote/BOM edge-cases are handled uniformly, and
  // one bad row doesn't abort the whole upload (Shopify parity).
  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Reset the input synchronously so re-selecting the same file re-fires.
    event.target.value = '';

    setImportProgress({ importing: true, success: 0, failed: 0, total: 0 });
    setImportErrors([]);

    try {
      const rowCount = await countCsvRows(file);
      if (rowCount === 0) {
        toast.error('That CSV appears to be empty. Add at least one product row and try again.');
        setImportProgress({ importing: false, success: 0, failed: 0, total: 0 });
        return;
      }

      // Tier pre-flight — cheap DB check that answers "will these rows
      // fit under my product cap?" before we upload a single byte.
      const preflightRes = await apiFetch('/merchants/products/import-preflight', {
        method: 'POST',
        body: JSON.stringify({ rowCount }),
      });
      const preflight = await preflightRes.json().catch(() => ({}));

      if (!preflightRes.ok) {
        toast.error(preflight?.error || 'Could not check your product limit. Please try again.');
        setImportProgress({ importing: false, success: 0, failed: 0, total: 0 });
        return;
      }

      if (preflight.wouldExceed) {
        const nextTierName = preflight.nextTier?.name || preflight.nextTier?.tier;
        const suffix = nextTierName ? ` Upgrade to ${nextTierName} for more.` : '';
        toast.warning(
          `This file has ${rowCount} product${rowCount === 1 ? '' : 's'}, ` +
          `but your ${preflight.currentTier} plan only has room for ${preflight.allowedRowCount} more ` +
          `(${preflight.currentCount}/${preflight.limit} used).${suffix}`,
          8000,
        );
        setImportProgress({ importing: false, success: 0, failed: 0, total: 0 });
        if (preflight.nextTier) navigateToUpgrade(navigate, preflight.nextTier);
        return;
      }

      setImportProgress({ importing: true, success: 0, failed: 0, total: rowCount });

      const form = new FormData();
      form.append('file', file, file.name || 'products.csv');
      const uploadRes = await apiFetch('/merchants/products/bulk-import', {
        method: 'POST',
        body: form,
      });
      const result = await uploadRes.json().catch(() => ({}));

      if (!uploadRes.ok) {
        // 402 from the server means the merchant crossed the limit between
        // preflight and upload (concurrent single-add). Surface the same
        // upgrade path so the UX matches the preflight branch.
        if (uploadRes.status === 402 && result?.code === 'PRODUCT_LIMIT_EXCEEDED') {
          toast.warning(result.error || 'Product limit exceeded during import.', 8000);
          if (result.nextTier) navigateToUpgrade(navigate, result.nextTier);
        } else {
          toast.error(result?.error || 'Failed to import products.');
        }
        setImportErrors([{ product: file.name || 'CSV', error: result?.error || 'Upload failed' }]);
        setImportProgress({ importing: false, success: 0, failed: 0, total: rowCount });
        return;
      }

      const summary = result.summary || { total: rowCount, imported: 0, failed: 0, skipped: 0 };
      setImportProgress({
        importing: false,
        success: summary.imported,
        failed: summary.failed,
        total: summary.total,
      });
      setImportErrors(
        (result.failed || []).map(f => ({
          product: `Row ${f.row}${f.name && f.name !== '(unnamed)' ? ` · ${f.name}` : ''}`,
          error: f.error,
        }))
      );

      if (summary.failed === 0 && summary.imported > 0) {
        toast.success(`${summary.imported} product${summary.imported === 1 ? '' : 's'} imported`);
      } else if (summary.imported > 0) {
        toast.warning(`${summary.imported} imported, ${summary.failed} failed. See error list below.`);
      } else if (summary.failed > 0) {
        toast.error(`Import failed for all ${summary.failed} rows. See error list below.`);
      }

      dispatch(fetchProducts());
      fetchProductLimits();
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('Something went wrong while importing. Please try again.');
      setImportErrors([{ product: 'CSV File', error: error.message || 'Unknown error' }]);
      setImportProgress({ importing: false, success: 0, failed: 0, total: 0 });
    }
  };

  // Auto-clear the success message after a few seconds.
  useEffect(() => {
    if (addSuccess) {
      const timer = setTimeout(() => setAddSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [addSuccess]);

  // ── Derived helpers used in render ──────────────────────────────────

  // Map an API product status to the badge variant + label we render.
  const renderStatusBadge = (rawStatus) => {
    const s = (rawStatus || '').toUpperCase();
    if (s === 'ACTIVE') return { cls: 'is-published', label: 'Published' };
    if (s === 'ARCHIVED') return { cls: 'is-archived', label: 'Archived' };
    return { cls: 'is-draft', label: 'Draft' };
  };

  // Variant count works for both legacy array format and the new
  // variant matrix shape { attributes, variants }.
  const getVariantCount = (product) => {
    if (!product.variations) return 0;
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      return product.variations.variants?.length || 0;
    }
    return product.variations.length || 0;
  };

  const formatCurrency = (n) => `₦${parseFloat(n || 0).toLocaleString()}`;

  const headerMeta = (() => {
    const total = products?.length || 0;
    const parts = [`${total} ${total === 1 ? 'product' : 'products'}`];
    if (productLimits && !productLimits.unlimited && productLimits.limit) {
      parts.push(`${productLimits.currentCount || 0}/${productLimits.limit} on plan`);
    }
    return parts.join(' · ');
  })();

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Shell>
      <div className="prod-page">

        {/* Header */}
        <header className="prod-header">
          <div className="prod-header-title">
            <h1>Products</h1>
            <p>{headerMeta}</p>
          </div>

          {/* Desktop actions */}
          <div className="prod-header-actions">
            <button
              type="button"
              className="prod-btn prod-btn-ghost"
              onClick={handleInventoryReport}
              title="Print inventory and stock count reports"
            >
              <ClipboardList size={14} />
              Inventory report
            </button>
            <button
              type="button"
              className="prod-btn prod-btn-ghost"
              onClick={handleExportCSV}
              title="Export products as CSV"
            >
              <Download size={14} />
              Export
            </button>
            <button
              type="button"
              className={`prod-btn prod-btn-ghost ${isLimitReached ? 'btn-limit-reached' : ''}`}
              onClick={handleImportClick}
              disabled={isLimitReached}
              title={isLimitReached ? 'Product limit reached — upgrade to import' : 'Import products from CSV'}
            >
              {isLimitReached && <Lock size={12} className="lock-icon" />}
              <Upload size={14} />
              Import
            </button>
            <button
              type="button"
              className={`prod-btn prod-btn-primary ${isLimitReached ? 'btn-limit-reached' : ''}`}
              onClick={handleAddProduct}
              disabled={isLimitReached}
              title={isLimitReached ? 'Product limit reached — upgrade to add more' : 'Add a new product'}
            >
              {isLimitReached && <Lock size={12} className="lock-icon" />}
              <Plus size={14} />
              Add product
            </button>
          </div>

          {/* Mobile: single trigger that expands to an action menu */}
          <div className="prod-mobile-actions">
            <button
              type="button"
              className="prod-btn prod-btn-primary"
              onClick={(e) => { e.stopPropagation(); setShowMobileActions(v => !v); }}
            >
              Actions
              <ChevronDown size={14} style={{ transform: showMobileActions ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            {showMobileActions && (
              <div className="prod-mobile-actions-menu" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="prod-mobile-actions-item"
                  disabled={isLimitReached}
                  onClick={() => { handleAddProduct(); setShowMobileActions(false); }}
                >
                  {isLimitReached ? <Lock size={16} /> : <Plus size={16} />}
                  Add product
                </button>
                <button
                  type="button"
                  className="prod-mobile-actions-item"
                  disabled={isLimitReached}
                  onClick={() => { handleImportClick(); setShowMobileActions(false); }}
                >
                  {isLimitReached ? <Lock size={16} /> : <Upload size={16} />}
                  Import products
                </button>
                <button
                  type="button"
                  className="prod-mobile-actions-item"
                  onClick={() => { handleInventoryReport(); setShowMobileActions(false); }}
                >
                  <ClipboardList size={16} />
                  Inventory report
                </button>
                <button
                  type="button"
                  className="prod-mobile-actions-item"
                  onClick={() => { handleExportCSV(); setShowMobileActions(false); }}
                >
                  <Download size={16} />
                  Export CSV
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Success message */}
        {addSuccess && (
          <div className="success-message" role="status">
            <Sparkles size={16} />
            <span>{addSuccess}</span>
          </div>
        )}

        {/* Stat cards */}
        <section className="prod-stats" aria-label="Product summary">
          <article className="prod-stat">
            <div className="prod-stat-icon"><DollarSign size={18} /></div>
            <div className="prod-stat-info">
              <span className="prod-stat-label">Retail value</span>
              <span className="prod-stat-value">{formatCurrency(summaryMetrics.totalRetailValue)}</span>
            </div>
          </article>
          <article className="prod-stat">
            <div className="prod-stat-icon"><Package size={18} /></div>
            <div className="prod-stat-info">
              <span className="prod-stat-label">Inventory value</span>
              <span className="prod-stat-value">{formatCurrency(summaryMetrics.totalInventoryValue)}</span>
            </div>
          </article>
          <article className="prod-stat is-info">
            <div className="prod-stat-icon"><ShoppingCart size={18} /></div>
            <div className="prod-stat-info">
              <span className="prod-stat-label">Products sold</span>
              <span className="prod-stat-value">{summaryMetrics.productsSold.toLocaleString()}</span>
            </div>
          </article>
          <article className="prod-stat is-warn">
            <div className="prod-stat-icon"><AlertTriangle size={18} /></div>
            <div className="prod-stat-info">
              <span className="prod-stat-label">Out of stock</span>
              <span className="prod-stat-value">{summaryMetrics.outOfStock.toLocaleString()}</span>
            </div>
          </article>
        </section>

        {/* List card: filters + table/mobile-cards + pagination */}
        <section className="prod-list-card">
          {/* Filters */}
          <div className="prod-filters">
            <div className="prod-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search products…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search products"
              />
            </div>
            <FilterDropdown
              value={statusFilter}
              onChange={setStatusFilter}
              ariaLabel="Filter by status"
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Draft' },
                { value: 'archived', label: 'Archived' },
              ]}
            />
            <FilterDropdown
              value={stockFilter}
              onChange={setStockFilter}
              ariaLabel="Filter by stock"
              options={[
                { value: 'all', label: 'All stock' },
                { value: 'in_stock', label: 'In stock' },
                { value: 'out_of_stock', label: 'Out of stock' },
              ]}
            />
            {/* "Clear filters" only appears when at least one filter
                is active — keeps the bar quiet by default. The old
                Refresh icon is gone; the list already re-fetches on
                every create / update / delete dispatch, so a manual
                refresh button was decorative. */}
            {(searchTerm || statusFilter !== 'all' || stockFilter !== 'all') && (
              <button
                type="button"
                className="prod-filter-clear"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setStockFilter('all');
                  setCurrentPage(1);
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Loading */}
          {status === 'loading' && (
            <div className="prod-loading">Loading products…</div>
          )}

          {/* Error */}
          {status !== 'loading' && error && (
            <div className="prod-error">Error loading products: {error}</div>
          )}

          {/* Empty */}
          {status !== 'loading' && !error && paginatedProducts.length === 0 && (
            <div className="prod-empty">
              <span className="prod-empty-icon"><Package size={28} /></span>
              <p className="prod-empty-title">
                {filteredAndSortedProducts.length === 0 && products.length > 0
                  ? 'No products match these filters'
                  : 'No products yet'}
              </p>
              <p className="prod-empty-desc">
                {filteredAndSortedProducts.length === 0 && products.length > 0
                  ? 'Try adjusting your search or filters.'
                  : 'Add your first product to start selling.'}
              </p>
            </div>
          )}

          {/* Desktop table — hidden on mobile via CSS */}
          {status !== 'loading' && !error && paginatedProducts.length > 0 && (
            <div className="prod-table-wrap">
              <table className="prod-table">
                <thead>
                  <tr>
                    <th className="prod-cell-checkbox">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(el) => { if (el) el.indeterminate = isSomeSelected; }}
                        onChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Collection</th>
                    <th>Variations</th>
                    <th>In stock</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.map(product => {
                    const productId = product.id || product._id;
                    const status = renderStatusBadge(product.status);
                    return (
                      <tr key={productId}>
                        <td className="prod-cell-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(productId)}
                            onChange={(e) => handleSelectProduct(productId, e.target.checked)}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                        <td>
                          <div className="prod-cell-product">
                            <div className="prod-cell-image">
                              {getPrimaryImageUrl(product) ? (
                                <img
                                  src={getPrimaryImageUrl(product)}
                                  alt={product.name}
                                  onError={(e) => { e.target.src = getFallbackImageUrl(); }}
                                />
                              ) : (
                                <ImageIcon size={18} />
                              )}
                            </div>
                            <div className="prod-cell-product-text">
                              <span className="prod-cell-product-name">{product.name}</span>
                              <span className="prod-cell-product-type">{product.productType || 'Physical'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="prod-cell-quick" data-quick-edit>
                          <button
                            type="button"
                            className="prod-quick-trigger"
                            onClick={(e) => handleQuickEditClick(e, productId, 'category')}
                            title="Click to change category"
                          >
                            {product.categoryRelation ? (
                              <div className="prod-tag-stack">
                                {product.categoryRelation.parent && (
                                  <span className="prod-tag-parent">{product.categoryRelation.parent.name}</span>
                                )}
                                <span className="prod-tag">{product.categoryRelation.name}</span>
                              </div>
                            ) : product.category ? (
                              <span className="prod-tag">{product.category}</span>
                            ) : (
                              <span className="prod-cell-muted">—</span>
                            )}
                            <ChevronDown size={12} />
                          </button>
                          {quickEditProduct === productId && quickEditType === 'category' && (
                            <div
                              className="dropdown-menu"
                              style={dropdownPosition[productId + 'category']}
                            >
                              {categories.length === 0 ? (
                                <div className="dropdown-empty">No categories yet</div>
                              ) : (
                                categories.map(cat => (
                                  <React.Fragment key={cat.id}>
                                    <button
                                      type="button"
                                      className={`dropdown-item ${(product.categoryId === cat.id || product.category === cat.name) ? 'active' : ''}`}
                                      onClick={() => handleQuickChange(productId, 'categoryId', cat.id)}
                                    >
                                      {cat.name}
                                    </button>
                                    {cat.children?.map(child => (
                                      <button
                                        key={child.id}
                                        type="button"
                                        className={`dropdown-item dropdown-item-child ${product.categoryId === child.id ? 'active' : ''}`}
                                        onClick={() => handleQuickChange(productId, 'categoryId', child.id)}
                                      >
                                        {child.name}
                                      </button>
                                    ))}
                                  </React.Fragment>
                                ))
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {product.collections && product.collections.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {product.collections.slice(0, 2).map((col, idx) => (
                                <span key={col.id || idx} className="prod-tag">{col.name}</span>
                              ))}
                              {product.collections.length > 2 && (
                                <span className="prod-tag-more">+{product.collections.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="prod-cell-muted">—</span>
                          )}
                        </td>
                        <td className="prod-cell-muted">{getVariantCount(product)}</td>
                        <td className="prod-cell-muted">
                          {product.quantity || 0}
                          {(product.expiredQuantity || 0) > 0 && (
                            <span className="prod-expired-chip" title="Past expiry — pending write-off">
                              {product.expiredQuantity} expired
                            </span>
                          )}
                        </td>
                        <td className="prod-cell-price">{formatCurrency(product.price)}</td>
                        <td className="prod-cell-quick" data-quick-edit>
                          <button
                            type="button"
                            className="prod-quick-trigger"
                            onClick={(e) => handleQuickEditClick(e, productId, 'status')}
                            title="Click to change status"
                          >
                            <span className={`prod-status-badge ${status.cls}`}>{status.label}</span>
                            <ChevronDown size={12} />
                          </button>
                          {quickEditProduct === productId && quickEditType === 'status' && (
                            <div
                              className="dropdown-menu"
                              style={{ ...dropdownPosition[productId + 'status'], minWidth: 140 }}
                            >
                              {[
                                { value: 'ACTIVE', label: 'Published' },
                                { value: 'DRAFT', label: 'Draft' },
                                { value: 'ARCHIVED', label: 'Archived' }
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={`dropdown-item ${product.status?.toUpperCase() === opt.value ? 'active' : ''}`}
                                  onClick={() => handleQuickChange(productId, 'status', opt.value)}
                                >
                                  <span className={`prod-status-badge ${renderStatusBadge(opt.value).cls}`}>{opt.label}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="prod-row-actions">
                            <button
                              type="button"
                              className="prod-row-action"
                              onClick={() => setSelectedProductForQR(product)}
                              title="View QR code"
                              aria-label="View QR code"
                            >
                              <QrCode size={14} />
                            </button>
                            <button
                              type="button"
                              className="prod-row-action"
                              onClick={() => handleEditProduct(product)}
                              title="Edit product"
                              aria-label="Edit product"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              type="button"
                              className="prod-row-action is-danger"
                              onClick={() => handleDelete(productId)}
                              disabled={deleting === productId}
                              title="Delete product"
                              aria-label="Delete product"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile card list — replaces the table below 768px */}
          {status !== 'loading' && !error && paginatedProducts.length > 0 && (
            <ul className="prod-mobile-list">
              {paginatedProducts.map(product => {
                const productId = product.id || product._id;
                const status = renderStatusBadge(product.status);
                return (
                  <li className="prod-mobile-item" key={productId}>
                    <div className="prod-mobile-image">
                      {getPrimaryImageUrl(product) ? (
                        <img
                          src={getPrimaryImageUrl(product)}
                          alt={product.name}
                          onError={(e) => { e.target.src = getFallbackImageUrl(); }}
                        />
                      ) : (
                        <ImageIcon size={22} />
                      )}
                    </div>
                    <div className="prod-mobile-body">
                      <div className="prod-mobile-title-row">
                        <h3 className="prod-mobile-title">{product.name}</h3>
                        <span className="prod-mobile-price">{formatCurrency(product.price)}</span>
                      </div>
                      <div className="prod-mobile-meta">
                        <span className={`prod-status-badge ${status.cls}`}>{status.label}</span>
                        {product.category && <><span className="prod-mobile-meta-dot" /><span>{product.category}</span></>}
                        <span className="prod-mobile-meta-dot" />
                        <span>
                          {product.quantity || 0} in stock
                          {(product.expiredQuantity || 0) > 0 && (
                            <span className="prod-expired-chip" title="Past expiry — pending write-off">
                              {product.expiredQuantity} expired
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="prod-mobile-bottom">
                        <span style={{ fontSize: 12, color: 'var(--color-gray-400)', textTransform: 'capitalize' }}>
                          {product.productType || 'Physical'}
                        </span>
                        <div className="prod-mobile-actions-row">
                          <button
                            type="button"
                            className="prod-row-action"
                            onClick={() => setSelectedProductForQR(product)}
                            aria-label="View QR code"
                          >
                            <QrCode size={14} />
                          </button>
                          <button
                            type="button"
                            className="prod-row-action"
                            onClick={() => handleEditProduct(product)}
                            aria-label="Edit product"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            type="button"
                            className="prod-row-action is-danger"
                            onClick={() => handleDelete(productId)}
                            disabled={deleting === productId}
                            aria-label="Delete product"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="prod-pagination" aria-label="Pagination">
              <span className="prod-pagination-info">
                Showing {visibleStart}–{visibleEnd} of {filteredAndSortedProducts.length}
              </span>
              <div className="prod-pagination-buttons">
                <button
                  type="button"
                  className="prod-page-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      className={`prod-page-btn ${currentPage === page ? 'is-active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                      aria-current={currentPage === page ? 'page' : undefined}
                      aria-label={`Page ${page}`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="prod-page-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </nav>
          )}
        </section>

        {/* ─── Floating bulk-action bar ───────────────────────────────
            Only renders when at least one product is checked. Sits
            above the merchant MobileBottomNav on <1024px (which
            reserves 72px + safe-area at the viewport bottom). */}
        {selectedProducts.length > 0 && (
          <div className="prod-bulk-bar" role="region" aria-label="Bulk actions">
            <span className="prod-bulk-count">
              {selectedProducts.length} selected
            </span>
            <div className="prod-bulk-actions">
              <button
                type="button"
                className="prod-bulk-btn"
                onClick={handleBulkPublish}
                title="Publish selected"
              >
                <Check size={14} />
                <span className="prod-bulk-btn-label">Publish</span>
              </button>
              <button
                type="button"
                className="prod-bulk-btn"
                onClick={handleBulkArchive}
                title="Archive selected"
              >
                <Archive size={14} />
                <span className="prod-bulk-btn-label">Archive</span>
              </button>
              <button
                type="button"
                className="prod-bulk-btn is-danger"
                onClick={handleBulkDelete}
                title="Delete selected"
              >
                <Trash2 size={14} />
                <span className="prod-bulk-btn-label">Delete</span>
              </button>
              <button
                type="button"
                className="prod-bulk-cancel"
                onClick={() => setSelectedProducts([])}
                aria-label="Clear selection"
                title="Clear selection"
              >
                <XIcon size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ─── Legacy product form sidebar (fallback flow) ─────────────── */}
        <Sidebar
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingId === 'new' ? 'Add New Product' : 'Edit Product'}
          subtitle={editingId === 'new' ? 'Fill in the details to add a new product' : 'Update product information'}
          width="xl"
          closeOnBackdropClick={false}
          closeOnEscape={false}
        >
          <ProductForm
            product={editingId === 'new' ? null : products.find(p => (p.id || p._id) === editingId)}
            onSubmit={onSubmitProduct}
            onCancel={handleCloseModal}
            loading={adding}
          />
        </Sidebar>

        {/* ─── Import modal ────────────────────────────────────────────── */}
        <Modal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportProgress({ importing: false, success: 0, failed: 0, total: 0 });
            setImportErrors([]);
          }}
          title="Import Products"
          size="md"
        >
          <div className="import-modal-content">
            <div className="import-instructions">
              <h4>How to import products:</h4>
              <ol>
                <li>Download the sample CSV template</li>
                <li>Fill in your product details following the template format</li>
                <li>Upload your CSV file</li>
              </ol>
            </div>

            <div className="import-sample-section">
              <p><strong>Supported Product Types:</strong></p>
              <ul className="product-types-list">
                <li><span className="type-badge physical">physical</span> Physical products (shipped)</li>
                <li><span className="type-badge digital">digital</span> Digital downloads</li>
                <li><span className="type-badge service">service</span> Services</li>
                <li><span className="type-badge appointment">appointment</span> Appointments/Consultations</li>
              </ul>

              <Button
                variant="outline"
                onClick={handleDownloadSampleCSV}
                style={{ width: '100%', marginTop: '16px' }}
              >
                <Download size={16} style={{ marginRight: '8px' }} />
                Download Sample CSV Template
              </Button>
            </div>

            <div className="import-upload-section">
              <input
                type="file"
                id="csv-import-input"
                accept=".csv"
                onChange={handleCSVImport}
                style={{ display: 'none' }}
              />
              <Button
                variant="primary"
                onClick={() => document.getElementById('csv-import-input').click()}
                disabled={importProgress.importing}
                style={{ width: '100%' }}
              >
                {importProgress.importing ? (
                  <>Importing... ({importProgress.success}/{importProgress.total})</>
                ) : (
                  <>
                    <Upload size={16} style={{ marginRight: '8px' }} />
                    Upload CSV File
                  </>
                )}
              </Button>
            </div>

            {importProgress.total > 0 && (
              <div className="import-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${((importProgress.success + importProgress.failed) / importProgress.total) * 100}%` }}
                  />
                </div>
                <div className="progress-stats">
                  <span className="success">✓ {importProgress.success} imported</span>
                  {importProgress.failed > 0 && (
                    <span className="failed">✗ {importProgress.failed} failed</span>
                  )}
                </div>
              </div>
            )}

            {importErrors.length > 0 && (
              <div className="import-errors">
                <h5>Import Errors:</h5>
                <ul>
                  {importErrors.map((err, index) => (
                    <li key={index}>
                      <strong>{err.product}:</strong> {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>

        {/* ─── Product QR modal ────────────────────────────────────────── */}
        <Modal
          isOpen={!!selectedProductForQR}
          onClose={() => setSelectedProductForQR(null)}
          title="Share product"
          size="sm"
        >
          <div className="prod-qr-modal">
            <p className="prod-qr-modal__hint">
              Scan this QR code to view{' '}
              <strong>{selectedProductForQR?.name || 'this product'}</strong> on your storefront.
            </p>
            {selectedProductForQR && (
              <QRCodeDisplay
                type="product"
                productId={selectedProductForQR.id || selectedProductForQR._id}
                productName={selectedProductForQR.name}
                size={200}
                showUrl
                showActions
              />
            )}
            <button
              type="button"
              className="prod-qr-modal__done"
              onClick={() => setSelectedProductForQR(null)}
            >
              Done
            </button>
          </div>
        </Modal>

      </div>
    </Shell>
  );
}
